import { useState, useCallback } from 'react';

import {
	useFindOrderMutation,
	useFindGroupOrderMutation,
	FindGroupOrderMutation,
	OrderFind,
	GroupOrderFindParams,
	InputLastNameWithTicket,
	useGetUserProfileQuery,
	CheckoutOrderFragment
} from '@websky/graphql';
import { Props } from './FindOrder';
import { GroupOrderParamsProps } from './types';
import { useSecureAccess } from '../../../../SecureAccess/SecureAccess.hook';
import { ApolloError } from 'apollo-boost';
import { GraphQLError } from 'graphql';
import { useConfig } from '../../../../context';

export function useFindOrder({ onSuccess }: Props) {
	const { data: userProfile, loading: userProfileLoading, refetch: getUserProfile } = useGetUserProfileQuery({
		fetchPolicy: 'no-cache'
	});
	const {
		global: { showBookingPreview }
	} = useConfig();

	const [findOrderMutation, { loading: findOrderLoading }] = useFindOrderMutation();
	const [findGroupOrderMutation, { loading: findGroupOrderLoading }] = useFindGroupOrderMutation();

	const secureAccess = useSecureAccess();

	const [orderPreview, setOrderPreview] = useState<CheckoutOrderFragment>(null);
	const [groupOrderParams, setGroupOrderParams] = useState<GroupOrderParamsProps>(null);
	const [groupOrderResponse, setGroupOrderResponse] = useState<FindGroupOrderMutation['FindGroupOrder']>(null);

	const getIsAuthorizedUser = useCallback(async () => {
		if (!userProfileLoading) {
			return !!userProfile?.CurrentUser;
		}

		try {
			const { data } = await getUserProfile();

			return !!data?.CurrentUser;
		} catch {
			return false;
		}
	}, [userProfile, userProfileLoading, getUserProfile]);

	const onHideOrderPreview = useCallback(() => {
		setOrderPreview(null);
	}, [setOrderPreview]);

	const onFindOrder = useCallback(
		async (params: OrderFind) => {
			onHideOrderPreview();

			const isAuthorized = await getIsAuthorizedUser();
			const hasBookingPreview = showBookingPreview && isAuthorized;

			try {
				const { data } = await findOrderMutation({
					variables: {
						params: {
							...params,
							saveInProfile: false
						}
					}
				});

				if (data?.FindOrder) {
					if (hasBookingPreview) {
						setOrderPreview(data.FindOrder);
					} else {
						onSuccess(data.FindOrder);
					}

					return true;
				} else {
					return false;
				}
			} catch (e) {
				if (e instanceof ApolloError && e.graphQLErrors?.length) {
					const errors = e.graphQLErrors as GraphQLError[];

					const isGroupOrder = errors.some(error => {
						return (
							(error as any)?.debugMessage?.includes('group order') ||
							(error as any)?.message?.includes('group order')
						);
					});

					if (secureAccess.isNeedToOpenSecureAccessModal(errors)) {
						secureAccess.open();
						secureAccess.setSubmitParams({ params: { id: params.id, secret: params.secret } });
					}

					if (isGroupOrder) {
						secureAccess.close();

						const groupOrder = await onFindGroupOrder({
							id: params.id,
							secret: params.secret,
							travellersDataForSplit: []
						});

						if (groupOrder) {
							setGroupOrderParams({ id: params.id, secret: params.secret });
							setGroupOrderResponse(groupOrder);

							return true;
						}

						secureAccess.close();
					}
				}

				return false;
			}
		},
		[
			findOrderMutation,
			setGroupOrderParams,
			setGroupOrderResponse,
			getIsAuthorizedUser,
			onHideOrderPreview,
			secureAccess.isNeedToOpenSecureAccessModal,
			secureAccess.setSubmitParams,
			secureAccess.open,
			secureAccess.close
		]
	);

	const onFindGroupOrder = useCallback(
		async (params: GroupOrderFindParams) => {
			try {
				const { data, errors } = await findGroupOrderMutation({
					variables: { params }
				});

				if (secureAccess.isNeedToOpenSecureAccessModal(errors)) {
					secureAccess.setSubmitParams({
						params: {
							id: params.id,
							secret: params.secret,
							travellersDataForSplit: params.travellersDataForSplit
						}
					});
					secureAccess.open();
				}

				if (data?.FindGroupOrder) {
					return data.FindGroupOrder;
				}

				return null;
			} catch (e) {
				console.log(e);

				return null;
			}
		},
		[
			findGroupOrderMutation,
			secureAccess.isNeedToOpenSecureAccessModal,
			secureAccess.setSubmitParams,
			secureAccess.open
		]
	);

	const onAcceptGroupOrder = useCallback(
		(travellersDataForSplit: InputLastNameWithTicket[]) =>
			onFindGroupOrder({
				id: groupOrderParams.id,
				secret: groupOrderParams.secret,
				travellersDataForSplit
			}),
		[onFindGroupOrder, groupOrderParams]
	);

	const onSubmitSecureAccess = useCallback(
		async (secret2fa: string) => {
			if (secureAccess.isFindGroupOrderVariablesGuard(secureAccess.submitParams)) {
				await onFindGroupOrder({
					id: secureAccess.submitParams.params.id,
					secret: secureAccess.submitParams.params.secret,
					travellersDataForSplit: secureAccess.submitParams.params.travellersDataForSplit
					// secret2fa
				});
			} else {
				await onFindOrder({
					id: secureAccess.submitParams.params.id,
					secret: secureAccess.submitParams.params.secret,
					secret2fa
				});
			}

			secureAccess.setShowError(true);
		},
		[onFindOrder, onFindGroupOrder, secureAccess]
	);

	const onResendSecureAccess = useCallback(async () => {
		if (secureAccess.isFindGroupOrderVariablesGuard(secureAccess.submitParams)) {
			await onFindGroupOrder({
				id: secureAccess.submitParams.params.id,
				secret: secureAccess.submitParams.params.secret,
				travellersDataForSplit: secureAccess.submitParams.params.travellersDataForSplit
			});
		} else {
			await onFindOrder({
				id: secureAccess.submitParams.params.id,
				secret: secureAccess.submitParams.params.secret
			});
		}
	}, [onFindOrder, onFindGroupOrder, secureAccess]);

	return {
		// data
		orderPreview,
		secureAccess,
		groupOrderParams,
		groupOrderResponse,
		isLoading: findOrderLoading || findGroupOrderLoading,
		isAuthorized: !!userProfile?.CurrentUser,
		/** Пользователь залогинен и является владельцем найденого заказа */
		isOrderOwner: orderPreview?.ownerId === userProfile?.CurrentUser?.userId,

		// methods
		setGroupOrderParams,
		setGroupOrderResponse,
		onFindOrder,
		onFindGroupOrder,
		onAcceptGroupOrder,
		onSubmitSecureAccess,
		onResendSecureAccess,
		onHideOrderPreview
	};
}
